import React, { useEffect, useState } from "react";
// d3 est utilisé indirectement via initializeMap
import { Box, Typography, Paper, Grid, Chip, useTheme, useMediaQuery, Drawer, IconButton, Divider, Fab, Button } from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ProvinceModal from "./ProvinceModal";
import { provinceMapping, productionStatsByProvince } from "./productionConfig";
import { initializeMap } from "./geoUtils";
import { alpha } from "@mui/material/styles";
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';

const FarmersMap = () => {
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [hoveredProvince, setHoveredProvince] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const theme = useTheme();
  // Utilisation de useMediaQuery pour les ajustements responsifs
  useMediaQuery(theme.breakpoints.down('sm'));

  // Détecte si l'utilisateur est sur mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Ne pas ouvrir automatiquement la légende au chargement sur mobile
      setLegendOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialise la carte
  useEffect(() => {
    // Petit délai pour s'assurer que le conteneur est bien rendu
    const timer = setTimeout(() => {
      initializeMap(isMobile, 
        // Callback pour le clic sur une province
        (provinceId) => {
          const provinceName = provinceMapping[provinceId] || "Province";
          const isEstuaire = provinceId === "GA1" || provinceName === "Estuaire";
          setSelectedProvince({
            id: provinceId,
            name: provinceName,
            stats: isEstuaire ? (productionStatsByProvince[provinceName] || []) : [],
            dataUnavailable: !isEstuaire,
          });
        },
        // Callback pour le survol d'une province
        (provinceId) => {
          if (provinceId) {
            const provinceName = provinceMapping[provinceId];
            const isEstuaire = provinceId === "GA1" || provinceName === "Estuaire";
            setHoveredProvince({
              id: provinceId,
              name: provinceName,
              isOperational: isEstuaire,
              stats: isEstuaire ? (productionStatsByProvince[provinceName] || []) : [],
            });
          } else {
            setHoveredProvince(null);
          }
        }
      );
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isMobile]);
  
  // Fonction pour obtenir les 3 principales cultures d'une province (actives pour l'Estuaire uniquement)
  const getTopCrops = (provinceName) => {
    if (!provinceName || (provinceName !== "Estuaire" && provinceName !== "GA1")) return [];
    
    const stats = productionStatsByProvince["Estuaire"] || [];
    return [...stats]
      .sort((a, b) => b.production - a.production)
      .slice(0, 3);
  };

  // Générer une couleur pour une culture
  const getCropColor = (cropName) => {
    const colors = {
      "Manioc": "#4CAF50",
      "Banane plantain": "#FFC107",
      "Igname": "#FF9800",
      "Taro": "#8BC34A",
      "Patate douce": "#CDDC39",
      "Cacao": "#795548",
      "Maïs": "#FFEB3B",
      "Arachide": "#FF5722",
      "Légumes verts": "#009688",
      "Tomate": "#F44336",
      "Pastèque": "#E91E63",
      "Mangue": "#9C27B0",
      "Poivron": "#673AB7",
      "Café": "#3F51B5"
    };
    
    return colors[cropName] || "#2196F3";
  };
  
  // La fonction getProvinceColor est maintenant importée depuis geoUtils.js

  // Gestion de la légende mobile
  const toggleLegend = () => {
    setLegendOpen(!legendOpen);
  };

  return (
    <Paper 
      elevation={3} 
      sx={{
        padding: { xs: 0, md: 2 },
        margin: { xs: '0 auto', md: '20px 0' },
        borderRadius: '12px',
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        position: 'relative',
        overflow: 'hidden',
        maxWidth: { xs: '100%', md: '100%' },
        boxSizing: 'border-box',
        width: '100%'
      }}
    >
      {/* Titre de la carte */}
      <Box
        sx={{
          textAlign: "center",
          margin: isMobile ? "0 0 4px" : { xs: "0 0 10px", md: "0 0 15px" },
          color: theme.palette.primary.main
        }}
      >
        <Typography 
          variant="h4" 
          component="div"
          sx={{ 
            fontSize: isMobile ? "1.5rem" : "2rem",
            fontWeight: "700",
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              width: '60px',
              height: '3px',
             
              left: 'calc(50% - 30px)',
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Carte des Productions Agricoles
        </Typography>

        {/* Mention de la phase pilote active uniquement sur l'Estuaire */}
        <Box sx={{ mt: 1.2, display: "flex", justifyContent: "center" }}>
          <Chip
            label="Données actives : Estuaire (Grand Libreville) • Phase Pilote"
            size="small"
            sx={{
              bgcolor: "#dcfce7",
              color: "#15803d",
              fontWeight: 700,
              fontSize: { xs: "0.74rem", sm: "0.82rem" },
              border: "1px solid #86efac",
              py: 0.4
            }}
          />
        </Box>

        {/* Lien direct vers la Centrale d'Achat & Marketplace */}
        <Box sx={{ mt: 1.5, mb: 1, display: "flex", justifyContent: "center" }}>
          <Button
            disabled
            variant="contained"
            startIcon={<StorefrontIcon sx={{ color: "#9ca3af !important" }} />}
            sx={{
              bgcolor: "#f3f4f6 !important",
              color: "#6b7280 !important",
              border: "1px solid #d1d5db",
              fontWeight: 700,
              fontSize: { xs: "0.8rem", sm: "0.92rem" },
              borderRadius: "24px",
              px: { xs: 2, sm: 2.8 },
              py: 0.8,
              boxShadow: "none !important",
              textTransform: "none",
              cursor: "not-allowed !important",
              pointerEvents: "auto !important",
              display: "inline-flex",
              alignItems: "center",
              gap: 1
            }}
          >
            <span>Centrale d'Achat & Marketplace</span>
            <Box
              component="span"
              sx={{
                bgcolor: "#9ca3af",
                color: "#ffffff",
                fontSize: "0.68rem",
                fontWeight: 700,
                px: 1,
                py: 0.25,
                borderRadius: "10px",
                textTransform: "none",
                letterSpacing: "0.2px",
                lineHeight: 1.2
              }}
            >
              Bientôt disponible
            </Box>
          </Button>
        </Box>
      </Box>

      {/* Bouton d'info pour mobile - bouton flottant pour accéder à la légende */}
      {!isMobile && (
        <Fab 
          size="medium" 
          color="primary" 
          aria-label="légende"
          onClick={toggleLegend}
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            zIndex: 1000,
            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(1, 69, 39, 0.7)' },
              '70%': { boxShadow: '0 0 0 10px rgba(1, 69, 39, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(1, 69, 39, 0)' }
            }
          }}
        >
          <MapIcon />
        </Fab>
      )}

      <Grid container spacing={0} sx={{ width: '100%', margin: 0, padding: 0 }}>
        {/* Carte */}
        <Grid item xs={12} md={8} sx={{ width: '100%', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'center', padding: 0, margin: 0 }}>
          {/* En version mobile, on ajoute un conteneur avec défilement contrôlé */}
          {isMobile ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                height: '400px',
                margin: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
                position: 'relative',
                touchAction: 'auto', /* Permet tous les gestes tactiles */
                WebkitOverflowScrolling: 'touch', /* Défilement fluide sur iOS */
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0))',
                  zIndex: 2,
                  pointerEvents: 'none' /* Permet les clics à travers */
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: 'linear-gradient(to top, rgba(255,255,255,0.7), rgba(255,255,255,0))',
                  zIndex: 2,
                  pointerEvents: 'none' /* Permet les clics à travers */
                }
              }}
            >
              {/* Indicateur de défilement pour mobile */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  color: theme.palette.text.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  pointerEvents: 'none' /* Permet les clics à travers */
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  Touchez pour sélectionner • Faites défiler pour naviguer
                </Typography>
              </Box>
              <div id="map" style={{ 
                width: '100%',
                height: '100%',
                margin: 0,
                padding: 0
              }}></div>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                position: "relative",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: '8px',
                padding: '0',
                margin: '0',
                backgroundColor: alpha('#f5f5f5', 0.5),
                overflow: 'hidden',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <div id="map" style={{ 
                width: '100%',
                maxWidth: '100%', 
                height: '540px',
                margin: '0',
                padding: '0',
                overflow: 'hidden'
              }}></div>
            </Box>
          )}
        </Grid>

        {/* Légende et informations - version desktop */}
        {!isMobile && (
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 2.5, 
                height: '100%', 
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  {hoveredProvince ? `Province : ${hoveredProvince.name}` : 'Données régionales de production'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  Phase Pilote active sur l'Estuaire (Grand Libreville)
                </Typography>
              </Box>

              {hoveredProvince ? (
                hoveredProvince.isOperational ? (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Chip
                        label="Données actives • Phase 1"
                        size="small"
                        sx={{
                          bgcolor: "#dcfce7",
                          color: "#15803d",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          border: "1px solid #86efac"
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Cliquez sur la province pour afficher la répartition complète et les graphiques de production.
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Principales cultures enregistrées :
                      </Typography>
                      {getTopCrops(hoveredProvince.name).map((crop, index) => (
                        <Chip 
                          key={index}
                          label={`${crop.culture} (${crop.production} t)`}
                          sx={{ 
                            m: 0.5, 
                            backgroundColor: alpha(getCropColor(crop.culture), 0.15),
                            border: `1px solid ${getCropColor(crop.culture)}`,
                            color: 'text.primary',
                            fontWeight: 600
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Chip
                        label="Phase 2 • Déploiement à venir"
                        size="small"
                        sx={{
                          bgcolor: "#f1f5f9",
                          color: "#64748b",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          border: "1px solid #cbd5e1"
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary', lineHeight: 1.6 }}>
                      Les données de production pour la province de <strong>{hoveredProvince.name}</strong> ne sont pas encore actives.
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', bgcolor: alpha('#64748b', 0.08), p: 1.5, borderRadius: '6px', lineHeight: 1.6 }}>
                      💡 Les indicateurs agricoles sont actuellement consolidés et déployés en priorité sur l'Estuaire (Grand Libreville). L'extension aux 8 autres provinces interviendra lors de la phase 2.
                    </Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Survolez ou cliquez sur une province pour consulter ses indicateurs de production.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    {Object.entries(provinceMapping).map(([code, name]) => {
                      const isEstuaire = code === "GA1" || name === "Estuaire";
                      return (
                        <Box 
                          key={code} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 0.8,
                            borderRadius: '6px',
                            bgcolor: isEstuaire ? alpha('#15803d', 0.08) : 'transparent',
                            border: isEstuaire ? '1px solid #86efac' : '1px solid transparent'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                backgroundColor: isEstuaire ? "#15803d" : "#94a3b8",
                                mr: 1.2 
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: isEstuaire ? 700 : 500, color: isEstuaire ? "#15803d" : "text.primary" }}>
                              {name}
                            </Typography>
                          </Box>
                          <Chip
                            label={isEstuaire ? "Données actives" : "Phase 2"}
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              bgcolor: isEstuaire ? "#15803d" : "#e2e8f0",
                              color: isEstuaire ? "#ffffff" : "#64748b",
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Légende mobile sous forme de drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={legendOpen}
          onClose={toggleLegend}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxHeight: '65vh',
              padding: '16px',
              boxShadow: '0px -4px 10px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease-in-out'
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                {hoveredProvince ? `Province : ${hoveredProvince.name}` : 'Données régionales de production'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Phase 1 active uniquement sur l'Estuaire
              </Typography>
            </Box>
            <IconButton onClick={toggleLegend} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider sx={{ my: 1.5 }} />
          
          {hoveredProvince ? (
            hoveredProvince.isOperational ? (
              <>
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    label="Données actives • Phase 1"
                    size="small"
                    sx={{
                      bgcolor: "#dcfce7",
                      color: "#15803d",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      border: "1px solid #86efac"
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Touchez la province pour ouvrir la répartition détaillée des productions.
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Principales cultures enregistrées :
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getTopCrops(hoveredProvince.name).map((crop, index) => (
                      <Chip 
                        key={index}
                        label={`${crop.culture} (${crop.production} t)`}
                        sx={{ 
                          backgroundColor: alpha(getCropColor(crop.culture), 0.2),
                          border: `1px solid ${getCropColor(crop.culture)}`,
                          color: 'text.primary',
                          fontWeight: 600,
                          mb: 0.5
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ py: 1 }}>
                <Chip
                  label="Phase 2 • Déploiement à venir"
                  size="small"
                  sx={{
                    bgcolor: "#f1f5f9",
                    color: "#64748b",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    border: "1px solid #cbd5e1",
                    mb: 1.5
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.6 }}>
                  Les données de récolte pour <strong>{hoveredProvince.name}</strong> seront publiées lors de la phase 2.
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', bgcolor: alpha('#64748b', 0.08), p: 1.5, borderRadius: '6px', lineHeight: 1.6 }}>
                  💡 Seule la Province de l'Estuaire (Grand Libreville) dispose actuellement de données opérationnelles actives.
                </Typography>
              </Box>
            )
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Touchez une province sur la carte pour consulter ses indicateurs de production.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {Object.entries(provinceMapping).map(([code, name]) => {
                  const isEstuaire = code === "GA1" || name === "Estuaire";
                  return (
                    <Box 
                      key={code} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 0.8,
                        borderRadius: '6px',
                        bgcolor: isEstuaire ? alpha('#15803d', 0.08) : 'transparent',
                        border: isEstuaire ? '1px solid #86efac' : '1px solid transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            backgroundColor: isEstuaire ? "#15803d" : "#94a3b8",
                            mr: 1 
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: isEstuaire ? 700 : 500, color: isEstuaire ? "#15803d" : "text.primary" }}>
                          {name}
                        </Typography>
                      </Box>
                      <Chip
                        label={isEstuaire ? "Données actives" : "Phase 2"}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          bgcolor: isEstuaire ? "#15803d" : "#e2e8f0",
                          color: isEstuaire ? "#ffffff" : "#64748b",
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Drawer>
      )}

      <ProvinceModal
        province={selectedProvince}
        onClose={() => setSelectedProvince(null)}
        isMobile={isMobile}
      />
    </Paper>
  );
};

export default FarmersMap;
